import * as React from 'react'
import { PageTitle } from '@hyper/page-title'
import { useDocumentTitle } from '@helpers/utils'
import { NavigationPath } from '@models/routes'
import { Card, CardBody } from 'reactstrap'
import { LoaderPlaceholder } from '@components/loader-placeholder'
import {
  BookingStatsParams,
  ImprovementsStats,
  StatsAppData,
  StatsAppDataImprovement,
} from '@modules/reservations/models'
import { formatDate } from '@helpers/date-helper'
import { addDays, startOfToday, subDays } from 'date-fns'
import { commonObjectGet } from '@store/actions/generic-actions'
import { RootState, useAppDispatch, useAppSelector } from '@store/index'
import { useApiRequest } from '@components/hooks/use-api-request'
import { useReportFormRequest } from '@modules/reports/hooks/use-report-request'
import { createReportTask } from '@store/actions/report-tasks-actions'
import { ButtonWithIcon } from '@components/button-with-icon'
import { useMenuActive } from '@components/hooks/use-menu-active'
import { CommonStatsBoxDetails } from '@modules/reservations/statistics/common/common-stats-box-details'
import { ImprovementsStatsTable } from '@modules/reservations/statistics/improvements-stats/stats-table/improvements-stats-table'
import { UserPermission } from '@models/dashboard'
import { useTimeCounter } from '@components/hooks/use-time-counter'

const initialDates = {
  date_after: subDays(startOfToday(), 1),
  date_before: addDays(startOfToday(), 7),
}

const REFRESH_INTERVAL_IN_SECONDS = 60
const pageTitle = 'Statystyki ulepszeń'

const breadCrumbItems = [
  {
    label: 'Rezerwacje',
    path: NavigationPath.ReservationList,
    active: false,
  },
  { label: pageTitle, path: '', active: true },
]

export const ReservationImprovementsStatsView: React.FC = () => {
  const [stats, setStats] = React.useState<ImprovementsStats | null>(null)
  const [params, setParams] = React.useState<Partial<BookingStatsParams>>({})
  const [appDataImprovement, setAppDataImprovements] = React.useState<StatsAppDataImprovement[]>([])

  const dispatch = useAppDispatch()

  useDocumentTitle(pageTitle)
  useMenuActive(NavigationPath.ReservationStats)

  const { booking_improvement_stats, booking_stats_app_data } = useAppSelector(
    (state: RootState) => state.appState.appData.urls.rent,
  )
  const reportUrl = useAppSelector(
    (state: RootState) => state.appState.appData.urls.reports.report_task_booking_improvement_stats_global_report,
  )

  const { isLoading: isFetchingAppData, action: fetchStatsAppData } = useApiRequest(async () => {
    const { improvements } = await commonObjectGet<StatsAppData>(booking_stats_app_data)
    setAppDataImprovements(improvements)
  })

  const { Counter, startCounter, stopCounter, clearCounter } = useTimeCounter({
    runOnInit: true,
    actionSecond: REFRESH_INTERVAL_IN_SECONDS,
    onTimeElapsed: () => {
      fetchStats(params)
    },
  })

  const { isLoading, action: fetchStats } = useApiRequest(async (data: Partial<BookingStatsParams> = {}) => {
    try {
      stopCounter()
      const params: Partial<BookingStatsParams> = {
        ...data,
        date_after: formatDate(data.date_after) || formatDate(initialDates.date_after),
        date_before: formatDate(data.date_before) || formatDate(initialDates.date_before),
      }

      setStats(await commonObjectGet<ImprovementsStats>(booking_improvement_stats, params))
    } finally {
      clearCounter()
      startCounter()
    }
  })

  const { action: handleReportClick } = useReportFormRequest(async () => {
    const reportTask = await dispatch(
      createReportTask([
        reportUrl,
        {
          ...params,
          date_after: params.date_after || initialDates.date_after,
          date_before: params.date_before || initialDates.date_before,
        },
      ]),
    )
    return reportTask
  }, 'notification')

  React.useEffect(() => {
    fetchStats()
    fetchStatsAppData()
  }, [])

  const handleDetailsFilter = React.useCallback((params: Partial<BookingStatsParams>) => {
    fetchStats(params)
    setParams(params)
  }, [])

  const filters = React.useMemo(() => params, [params])

  return (
    <>
      <PageTitle breadCrumbItems={breadCrumbItems} title={pageTitle} />
      <Card className="h-100">
        <CardBody className="d-flex flex-column">
          <LoaderPlaceholder content={stats && !isFetchingAppData}>
            {stats && (
              <>
                <strong className="mb-2 d-block text-secondary font-18">Ulepszenia szczegółowo:</strong>
                <CommonStatsBoxDetails
                  onFilter={handleDetailsFilter}
                  isLoading={isLoading}
                  resortViewPermission={UserPermission.BookingImprovementStatisticsCanViewAllResorts}
                  initialDates={initialDates}
                >
                  <ImprovementsStatsTable details={stats.details} improvements={appDataImprovement} filters={filters} />
                </CommonStatsBoxDetails>
                <Counter className="time-counter__statistics" />
                <p className="text-right">
                  <ButtonWithIcon
                    icon="uil-download-alt mr-1"
                    color="secondary"
                    text="Pobierz"
                    handleClick={handleReportClick}
                  />
                </p>
              </>
            )}
          </LoaderPlaceholder>
        </CardBody>
      </Card>
    </>
  )
}
